/**
 * Kopplung „Kartierer-Name ↔ Punkt-Eigentum".
 *
 * Hintergrund: Das Editier-Recht eines Bearbeiters an einem Punkt hängt an
 * `workflow.createdBy.id` (Keycloak-`sub`), NICHT am Datenfeld
 * `header.aufnahme.kartierer` (freier Anzeigename). Wird nur der Name geändert,
 * läuft der benannte Kartierer und der tatsächliche Eigentümer auseinander —
 * der genannte Kartierer kann „seinen" Punkt dann nicht bearbeiten.
 *
 * Deshalb wird der Kartierer nicht mehr frei getippt, sondern als echtes
 * Projekt-Mitglied ausgewählt; Name UND `createdBy.id` werden immer gemeinsam
 * gesetzt. Diese Datei stellt die reine Kopplungs-Logik bereit (für Batch-Editor
 * und Einzel-Formular gleichermaßen) — frei von Angular/DOM, damit test-bar.
 *
 * Wichtig: Als Kartierer-Wert wird der **Anzeigename** (Vorname Nachname, sonst
 * Username) verwendet — nicht der Login-Username. Bestandspunkte tragen historisch
 * Anzeigenamen (bei Anlage = `user.name`), und nur so passt die Auswahl-Option zum
 * vorhandenen Wert (sonst erscheint der alte Wert als zusätzliche „Doppel"-Option).
 */

import { BatchEditField, BatchEditFieldWriteResult, getByPath } from '@luzula/luzula-lib';
import { AufnahmePunktDocument } from '../../data-model';

/** Schlüssel/Pfad des Kartierer-Feldes im AufnahmePunktDocument. */
export const KARTIERER_FIELD_KEY = 'header.aufnahme.kartierer';

/**
 * Ein auswählbarer Kartierer = Projekt-Mitglied (Owner oder Bearbeiter).
 * `id` = Keycloak-`sub` (→ `workflow.createdBy.id`),
 * `name` = Anzeigename (→ `header.aufnahme.kartierer`).
 */
export interface KartiererOption {
	id: string;
	name: string;
}

/** Mitglied, wie es im Projekt-Doc steht (owner/bearbeiter-Eintrag). */
interface MemberLike {
	id?: string;
	username?: string;
	firstName?: string;
	lastName?: string;
}

/** Minimaler Projekt-Doc-Ausschnitt für die Mitglieder-Auflösung. */
export interface ProjectMembersDoc {
	owner?: MemberLike;
	bearbeiter?: MemberLike[];
}

/**
 * Anzeigename eines Mitglieds: „Vorname Nachname", sonst Username, sonst id.
 * (Gleiche Logik wie im Mitglieder-Auswahl-Modal, damit Picker und Auswahl
 * konsistent denselben Namen zeigen.)
 */
export function memberDisplayName(m: MemberLike): string {
	const full = [m.firstName, m.lastName].filter(Boolean).join(' ').trim();
	return full || m.username || m.id || '';
}

/**
 * Auswählbare Kartierer = Projekt-Mitglieder, die Punkte besitzen/bearbeiten
 * können: Owner + Bearbeiter. Wert = Anzeigename. Dedupliziert nach `id`;
 * Einträge ohne id/Name werden verworfen. Kontrolleure zählen nicht.
 */
export function kartiererOptionsFromProject(doc: ProjectMembersDoc | null | undefined): KartiererOption[] {
	if (!doc) return [];
	const byId = new Map<string, KartiererOption>();
	for (const c of [doc.owner, ...(doc.bearbeiter ?? [])]) {
		const name = c ? memberDisplayName(c) : '';
		if (c?.id && name && !byId.has(c.id)) {
			byId.set(c.id, { id: c.id, name });
		}
	}
	return [...byId.values()];
}

/**
 * Setzt Kartierer-Name UND Punkt-Eigentum (`workflow.createdBy.id`) gemeinsam.
 *
 * `createdBy.id` wird nur überschrieben, wenn der Punkt einen Workflow-Block hat.
 * Bestandspunkte ohne Workflow kennen kein Eigentum — dort wird ausschließlich
 * der Name gesetzt (`createdBy.time` bleibt in jedem Fall unangetastet).
 */
export function applyKartiererOwner(doc: AufnahmePunktDocument, member: KartiererOption): void {
	// header.aufnahme ist im Datenmodell Pflicht; defensiv trotzdem absichern,
	// falls ein Alt-/Teildokument das Feld nicht trägt.
	if (!doc.header.aufnahme) {
		(doc.header as { aufnahme?: unknown }).aufnahme = { datum: '', kartierer: '' };
	}
	doc.header.aufnahme.kartierer = member.name;

	const createdBy = doc.workflow?.createdBy;
	if (createdBy) {
		createdBy.id = member.id;
	}
}

/**
 * Baut das Batch-Edit-Feld „Kartierer" als Nutzer-Auswahl (enum), dessen
 * `setValue` Name + Eigentum koppelt.
 *
 * - Wert/Label = Anzeigename (menschenlesbar; passt zu Bestandswerten; preview/
 *   Historie/Undo arbeiten so mit lesbaren Werten statt rohen UUIDs).
 * - `setValue` löst den gewählten Namen über die Mitgliederliste zur `sub` auf
 *   und setzt beides. Ein Name ohne passendes Mitglied wird abgelehnt
 *   (`{ ok: false }`) — so kann das Eigentum nie an einen Nicht-Member kippen,
 *   und ein Undo auf einen ausgeschiedenen früheren Kartierer wird sauber
 *   übersprungen statt inkonsistent geschrieben.
 *
 * NUR aufrufen, wenn der aktuelle User umhängen darf (Owner/Entwickler);
 * sonst das Feld gar nicht erst anbieten.
 */
export function buildKartiererBatchField(members: KartiererOption[]): BatchEditField {
	const byName = new Map(members.map(m => [m.name, m]));
	return {
		key: KARTIERER_FIELD_KEY,
		label: 'Kartierer',
		group: 'Aufnahme',
		type: 'enum',
		enumValues: members.map(m => ({ value: m.name, label: m.name })),
		hint: 'Setzt zugleich den Punkt-Eigentümer (workflow.createdBy). Auswahl nur aus Projekt-Mitgliedern.',
		getValue: (doc) => getByPath(doc, KARTIERER_FIELD_KEY),
		setValue: (doc, value): BatchEditFieldWriteResult => {
			const member = byName.get(String(value));
			if (!member) {
				return { ok: false, reason: 'Kein Projekt-Mitglied mit diesem Namen — Eigentum nicht setzbar' };
			}
			applyKartiererOwner(doc as unknown as AufnahmePunktDocument, member);
			return { ok: true };
		}
	};
}
